import React from 'react';
import styled from 'styled-components';
import { Container, Row, Col } from 'react-bootstrap';
import { Link } from 'gatsby';
import { device } from '../utils/breakpoints';

const BenefitsWrapper = styled.section`
  background: linear-gradient(135deg, #0A1929 0%, #0F2744 100%);
  padding: 100px 0;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: 
      radial-gradient(circle at 30% 50%, rgba(255, 215, 0, 0.1) 0%, transparent 50%),
      radial-gradient(circle at 70% 50%, rgba(124, 179, 66, 0.08) 0%, transparent 50%);
    pointer-events: none;
  }
  
  @media ${device.tablet} {
    padding: 60px 0;
  }
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 60px;
  position: relative;
  z-index: 1;
`;

const SectionTitle = styled.h2`
  font-size: 42px;
  font-weight: 700;
  color: #ffffff;
  margin-bottom: 16px;
  
  @media ${device.tablet} {
    font-size: 32px;
  }
`;

const SectionSubtitle = styled.p`
  font-size: 18px;
  color: rgba(255, 255, 255, 0.85);
  max-width: 700px;
  margin: 0 auto 16px;
  
  @media ${device.tablet} {
    font-size: 16px;
  }
`;

const PrivacyBadge = styled.div`
  display: inline-block;
  background: linear-gradient(135deg, #7CB342 0%, #5A9A2A 100%);
  color: white;
  padding: 10px 24px;
  border-radius: 8px;
  font-size: 15px;
  font-weight: 600;
  box-shadow: 0 4px 12px rgba(124, 179, 66, 0.4);
  
  @media ${device.tablet} {
    font-size: 14px;
    padding: 8px 20px;
  }
`;

const TeamCard = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border-radius: 20px;
  padding: 48px;
  height: 100%;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: all 0.4s ease;
  position: relative;
  z-index: 1;
  
  &:hover {
    transform: translateY(-8px);
    background: rgba(255, 255, 255, 0.08);
    border-color: rgba(255, 215, 0, 0.3);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2);
  }
  
  @media ${device.tablet} {
    padding: 40px 32px;
    margin-bottom: 24px;
  }
`;

const TeamHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  margin-bottom: 24px;
`;

const TeamIcon = styled.div`
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, #FFD700 0%, #FF6B35 100%);
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 40px;
  flex-shrink: 0;
  box-shadow: 0 8px 20px rgba(255, 107, 53, 0.4);
`;

const TeamTitle = styled.h3`
  font-size: 28px;
  font-weight: 700;
  color: #ffffff;
  margin: 0;
  
  @media ${device.tablet} {
    font-size: 24px;
  }
`;

const TeamDescription = styled.p`
  font-size: 18px;
  line-height: 1.8;
  color: rgba(255, 255, 255, 0.85);
  margin-bottom: 28px;
  
  @media ${device.tablet} {
    font-size: 16px;
  }
`;

const BenefitsList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 32px 0;
`;

const BenefitItem = styled.li`
  font-size: 16px;
  color: rgba(255, 255, 255, 0.9);
  padding: 12px 0;
  padding-left: 32px;
  position: relative;
  line-height: 1.6;
  
  &:before {
    content: '✓';
    position: absolute;
    left: 0;
    color: #7CB342;
    font-weight: 700;
    font-size: 20px;
  }
  
  @media ${device.tablet} {
    font-size: 15px;
  }
`;

const TeamCTA = styled(Link)`
  display: inline-block;
  background: transparent;
  color: #FFD700;
  padding: 12px 28px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  text-decoration: none;
  border: 2px solid rgba(255, 215, 0, 0.5);
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(255, 215, 0, 0.15);
    border-color: #FFD700;
    color: #FFD700;
    text-decoration: none;
    transform: translateX(4px);
  }
`;

const TeamBenefitsSection = () => {
  return (
    <BenefitsWrapper>
      <Container>
        <SectionHeader>
          <SectionTitle>Built for Every Team</SectionTitle>
          <SectionSubtitle>
            Empowering organizations with governed operations that enable safe innovation and reduce long-term risk
          </SectionSubtitle>
          <PrivacyBadge>
            🔒 All solutions deployed in YOUR private environment
          </PrivacyBadge>
        </SectionHeader>
        
        <Row className="g-4">
          <Col lg={6} md={12}>
            <TeamCard>
              <TeamHeader>
                <TeamIcon>👨‍💻</TeamIcon>
                <TeamTitle>Engineering Teams</TeamTitle>
              </TeamHeader>
              <TeamDescription>
                Accelerate innovation through experimentation facilitated by open-source technologies. 
                Ametnes makes it easy to access cutting-edge open-source technologies-all deployed in your 
                private environment with built-in governance from the start, not added later.
              </TeamDescription>
              <BenefitsList>
                <BenefitItem>Access 200+ cutting-edge open-source technologies in your private environment</BenefitItem>
                <BenefitItem>Built-in governance from deployment, not added later</BenefitItem>
                <BenefitItem>Operational patterns that hold up under audit and long-term pressure</BenefitItem>
                <BenefitItem>Deploy AI models, data pipelines, and dev tools with consistent operations</BenefitItem>
                <BenefitItem>Full control and data privacy - your data never leaves your infrastructure</BenefitItem>
              </BenefitsList>
              <TeamCTA to="/platform/ai-software">
                Explore Engineering Solutions →
              </TeamCTA>
            </TeamCard>
          </Col>
          
          <Col lg={6} md={12}>
            <TeamCard>
              <TeamHeader>
                <TeamIcon>💼</TeamIcon>
                <TeamTitle>Business Teams</TeamTitle>
              </TeamHeader>
              <TeamDescription>
                Ametnes offers a wide range of affordable open-source software to help 
                run every aspect of any business efficiently and cost-effectively-all securely hosted 
                in your private cloud or on-premise infrastructure with standardized operations that reduce risk.
              </TeamDescription>
              <BenefitsList>
                <BenefitItem>Comprehensive ERP, CRM, and HRMS solutions in your private environment</BenefitItem>
                <BenefitItem>Consistent operations reduce long-term risk and operational complexity</BenefitItem>
                <BenefitItem>Finance, inventory, and project management tools with complete data control</BenefitItem>
                <BenefitItem>No per-user licensing fees, vendor lock-in, or data sharing</BenefitItem>
                <BenefitItem>Scale affordably while maintaining governed operations and audit readiness</BenefitItem>
              </BenefitsList>
              <TeamCTA to="/platform/business-software">
                Explore Business Solutions →
              </TeamCTA>
            </TeamCard>
          </Col>
        </Row>
      </Container>
    </BenefitsWrapper>
  );
};

export default TeamBenefitsSection;

